import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface LogoSliderProps {
  logos: {
    id: number;
    name: string;
    image: string;
    alt: string;
  }[];
  speed?: number; // velocità in secondi
  pauseOnHover?: boolean;
  className?: string;
}

const LogoSlider: React.FC<LogoSliderProps> = ({
  logos = [],
  speed = 30, // durata completa dell'animazione in secondi
  pauseOnHover = true,
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // Calcola la larghezza totale del contenitore dei loghi quando il componente viene montato
  useEffect(() => {
    if (sliderRef.current) {
      // Aggiorna la larghezza quando il componente viene montato
      const updateWidth = () => {
        if (sliderRef.current) {
          setWidth(sliderRef.current.scrollWidth - sliderRef.current.offsetWidth);
        }
      };

      updateWidth();
      // Aggiorna la larghezza quando la finestra viene ridimensionata
      window.addEventListener('resize', updateWidth);
      
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }
  }, [logos]);

  // Verifica che logos sia definito e non vuoto
  if (!logos || logos.length === 0) {
    return null; // Non renderizza nulla se non ci sono loghi
  }

  // Duplica i loghi tre volte per garantire uno scorrimento infinito senza interruzioni
  const duplicatedLogos = [...logos, ...logos, ...logos];

  return (
    <div 
      className={`w-full overflow-hidden ${className}`}
      onMouseEnter={() => pauseOnHover && setIsHovered(true)}
      onMouseLeave={() => pauseOnHover && setIsHovered(false)}
    >
      <div className="relative" ref={sliderRef}>
        <motion.div
          className="flex items-center"
          animate={{
            x: isHovered ? 0 : [-width / 3, -width * 2 / 3]
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: speed,
              ease: "linear",
              repeatDelay: 0
            }
          }}
        >
          {duplicatedLogos.map((logo, index) => (
            <div
              key={`${logo.id}-${index}`}
              className="flex-shrink-0 px-8 py-4"
              style={{ width: `${100 / (logos.length)}%`, minWidth: '180px' }}
            >
              <div className="p-2 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 h-32 flex items-center justify-center border border-gray-100">
                {logo.image ? (
                  <img
                    src={logo.image}
                    alt={logo.alt || logo.name}
                    className="max-h-32 max-w-full object-contain"
                  />
                ) : (
                  <div className="text-xl font-semibold text-primary">{logo.name}</div>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default LogoSlider; 